import {
  DatePicker as NextUIDatePicker,
  DatePickerProps as NextUIDatePickerProps,
} from "@heroui/react";
import { CalendarIcon } from "lucide-react";
import {
  CalendarDateInput,
  convertCalendarDateToUnix,
  toCalendarDate,
} from "../../utils/date";
import {
  getCalendarClassNames,
  getDatePickerClassNames,
} from "../../utils/styles";

type Color = Extract<NextUIDatePickerProps["color"], "default">;
type LabelPlacement = Extract<
  NextUIDatePickerProps["labelPlacement"],
  "outside"
>;
type Variant = Extract<NextUIDatePickerProps["variant"], "bordered">;

export type DatePickerProps = Omit<
  NextUIDatePickerProps,
  | "color"
  | "labelPlacement"
  | "maxValue"
  | "minValue"
  | "selectorButtonPlacement"
  | "selectorIcon"
  | "value"
  | "variant"
> & {
  color?: Color;
  granularity?: string;
  labelPlacement?: LabelPlacement;
  maxValue?: number;
  minValue?: number;
  onChange?: (value: number | null) => void;
  ref?: React.Ref<HTMLDivElement>;
  value?: number | null;
  variant?: Variant;
};

export function DatePicker({ ref, ...props }: DatePickerProps) {
  const { granularity, maxValue, minValue, onChange, value, ...rest } = props;

  const handleChange = (calendarDate: CalendarDateInput) => {
    if (!onChange) return;
    onChange(convertCalendarDateToUnix(calendarDate, granularity));
  };

  return (
    <div ref={ref} className="outline-none" tabIndex={-1}>
      <NextUIDatePicker
        {...rest}
        calendarProps={{
          classNames: getCalendarClassNames(),
          visibleMonths: 1,
          weekdayStyle: "short",
        }}
        classNames={getDatePickerClassNames(props.size)}
        color="default"
        data-testid={props.name}
        labelPlacement="outside"
        maxValue={
          toCalendarDate(
            maxValue,
            granularity,
          ) as NextUIDatePickerProps["maxValue"]
        }
        minValue={
          toCalendarDate(
            minValue,
            granularity,
          ) as NextUIDatePickerProps["minValue"]
        }
        selectorButtonPlacement="end"
        selectorButtonProps={{ color: "primary" }}
        selectorIcon={<CalendarIcon size={16} />}
        value={
          toCalendarDate(value, granularity) as NextUIDatePickerProps["value"]
        }
        variant="bordered"
        onChange={handleChange}
      />
    </div>
  );
}

DatePicker.displayName = "RecareUI.DatePicker";
